import { z } from "zod";
import { ReportProductStatusSchema, TicketTypeSchema } from "./reports.js";
/**
 * v13.7 — TICKETS « Signaler un bug » / « Proposer une fonctionnalité ».
 *
 * LA FRONTIÈRE CLIENT→SERVEUR d'un ticket. Un ticket est un dossier `Report` de
 * `type` BUG ou FEATURE : il partage la timeline, la page admin et le suivi
 * auteur des signalements (packages/protocol/src/reports.ts). Ce fichier ne
 * porte QUE ce qui lui est propre : le corps de dépôt et le contexte technique.
 *
 * RÈGLES DE MINIMISATION (AGENTS.md §3), non négociables :
 *  - Zod STRICT partout (`.strict()`) : un champ inconnu est REJETÉ, jamais
 *    ignoré silencieusement — la surface d'un ticket est exactement celle-ci.
 *  - Tout est BORNÉ : ni titre, ni corps, ni ligne de contexte ne sont des champs
 *    libres illimités. C'est ce qui protège le rendu admin (le back valide et
 *    borne, le front échappe) et la base (pas de JSON qui gonfle sans fin).
 *  - JAMAIS de contenu de conversation, de photo de conversation, ni d'identifiant
 *    d'interlocuteur dans un ticket. Un ticket parle du PRODUIT, pas des gens.
 */
// ── Contexte technique ───────────────────────────────────────────────────────
export const TicketPlatformSchema = z.enum(["ios", "android", "web"]);
/** Bornes des chaînes de diagnostic — des libellés, pas des champs libres. */
export const MAX_TICKET_CONTEXT_FIELD = 80;
export const MAX_TICKET_TITLE_LENGTH = 140;
export const MIN_TICKET_TITLE_LENGTH = 3;
export const MAX_TICKET_BODY_LENGTH = 5000;
export const MIN_TICKET_BODY_LENGTH = 10;
const contextField = () => z.string().trim().min(1).max(MAX_TICKET_CONTEXT_FIELD);
/**
 * Contexte technique DÉCLARÉ PAR LE CLIENT. `.strict()` : la version de l'app,
 * la plateforme, la version de l'OS et l'écran d'origine — rien d'autre. Ce que
 * le SERVEUR sait mieux que le client (sa propre version et son sha de build)
 * n'est PAS ici : il est injecté côté serveur (routes/tickets.ts), jamais cru du
 * client. Aucun champ n'est obligatoire : un contexte partiel vaut mieux qu'un
 * ticket refusé pour un diagnostic manquant.
 */
export const ClientTicketContextSchema = z
    .object({
    appVersion: contextField().optional(),
    platform: TicketPlatformSchema.optional(),
    osVersion: contextField().optional(),
    /** Écran depuis lequel le ticket a été ouvert (aide au diagnostic). */
    originScreen: contextField().optional(),
})
    .strict();
/**
 * Contexte technique TEL QU'IL EST STOCKÉ et RENDU à l'admin : les champs client
 * + les deux champs serveur (version + sha de l'API au moment du dépôt). C'est la
 * forme que porte `Report.ticketContext` (Json) et que l'admin lit.
 */
export const TicketTechnicalContextSchema = ClientTicketContextSchema.extend({
    /** Version de l'API au dépôt (VERSION), posée serveur. */
    apiVersion: z.string().max(MAX_TICKET_CONTEXT_FIELD).optional(),
    /** Sha du build API au dépôt, posé serveur. */
    apiSha: z.string().max(MAX_TICKET_CONTEXT_FIELD).optional(),
}).strict();
// ── Dépôt ────────────────────────────────────────────────────────────────────
/**
 * `POST /v1/tickets`. `.strict()` : type + titre + corps + contexte optionnel.
 * Pas de `reportedUserId`, pas de `sessionId`, pas de `groupId` — un ticket ne
 * vise personne et ne cite aucune conversation.
 */
export const CreateTicketSchema = z
    .object({
    type: TicketTypeSchema,
    title: z.string().trim().min(MIN_TICKET_TITLE_LENGTH).max(MAX_TICKET_TITLE_LENGTH),
    body: z.string().trim().min(MIN_TICKET_BODY_LENGTH).max(MAX_TICKET_BODY_LENGTH),
    context: ClientTicketContextSchema.optional(),
})
    .strict();
// ── Réponses (le contrat que le client peut supposer) ────────────────────────
/** Une pièce jointe de ticket, servie par URL signée (jamais un blob nu). */
export const TicketAttachmentSchema = z.object({
    id: z.string(),
    url: z.string(),
    thumbUrl: z.string().nullable(),
    mimeType: z.string(),
    fileName: z.string(),
    width: z.number().nullable(),
    height: z.number().nullable(),
});
/** Une étape de la timeline TELLE QUE LA VOIT L'AUTEUR : statut, date, motif
 *  public. Aucune note interne, aucun nom d'admin (identique aux signalements). */
export const TicketTimelineStepSchema = z.object({
    status: ReportProductStatusSchema,
    at: z.string(),
    publicReason: z.string().nullable(),
});
export const MyTicketSchema = z.object({
    id: z.string(),
    reference: z.string(),
    type: TicketTypeSchema,
    title: z.string(),
    body: z.string(),
    status: ReportProductStatusSchema,
    createdAt: z.string(),
    resolvedAt: z.string().nullable(),
    attachment: TicketAttachmentSchema.nullable(),
    timeline: z.array(TicketTimelineStepSchema),
});
//# sourceMappingURL=tickets.js.map